import { Popover, PopoverProps } from 'antd';
import { ReactNode, cloneElement, isValidElement, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';

import { useTheme } from '@desygner/ui-common-components';
import { BottomSheet } from '@desygner/ui-common-components-next';

import { AccessSwitchMode, ShareAccessLevel } from './ShareAccess.types';
import { ShareAccessContent, ShareAccessContentProps } from './ShareAccessContent';

export type ShareAccessProps = Pick<PopoverProps, 'align' | 'placement'> & {
  shareLink: string;
  access: ShareAccessLevel;
  open: boolean;
  onAccessChange: ShareAccessContentProps['onSelectedAccessChange'];
  features?: ShareAccessContentProps['features'];
  accessSwitchMode?: AccessSwitchMode;
  disablePortal?: boolean;
  onOpenChange: (open: boolean) => void;
  children?: ReactNode;
  projectName: string;
  isOwner?: boolean;
  projectType?: 'design' | 'template';
  isTeamShared?: boolean;
};

export function ShareAccess({
  children,
  open,
  align,
  placement,
  features,
  accessSwitchMode,
  disablePortal = false,
  onOpenChange,
  access,
  onAccessChange,
  shareLink,
  projectName,
  projectType = 'design',
  isOwner,
  isTeamShared,
}: ShareAccessProps) {
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery({
    minWidth: 0,
    maxWidth: theme.Screen.Size.Large,
  });

  const handleCloseShareAccess = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const shareAccessContent = (
    <ShareAccessContent
      shareLink={shareLink}
      selectedAccess={access}
      onSelectedAccessChange={onAccessChange}
      features={features}
      accessSwitchMode={accessSwitchMode}
      projectName={projectName}
      projectType={projectType}
      isOwner={isOwner}
      isTeamShared={isTeamShared}
    />
  );

  if (isTabletOrMobile) {
    return (
      <>
        {isValidElement(children) &&
          cloneElement(children as React.ReactElement, {
            onClick: (e: React.MouseEvent) => {
              (children as React.ReactElement)?.props?.onClick?.(e);
              onOpenChange?.(true);
            },
          })}
        <BottomSheet
          height={'auto'}
          isOpened={open}
          onHandleClose={handleCloseShareAccess}
          content={shareAccessContent}
        />
      </>
    );
  }

  return (
    <Popover
      open={open}
      placement={placement}
      popupVisible={open}
      align={align}
      arrow={false}
      zIndex={500}
      trigger={'click'}
      onOpenChange={onOpenChange}
      getPopupContainer={
        disablePortal ? (trigger) => trigger.parentElement as HTMLElement : undefined
      }
      content={shareAccessContent}
    >
      {children}
    </Popover>
  );
}
